import { useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import {
  X, Plus, Trash2, ChevronDown, ArrowDown, GripVertical,
  Filter, Wand2, Type, Hash, ToggleLeft, Search,
  AtSign, Phone, Link2, Scissors, Replace, Calendar, Clock, Eye, Globe,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { FieldFormatConfig } from '@/types/field-mapping-flow';
import {
  TIMEZONE_OPTIONS,
  isDateTimeFormatType,
  getFormatPresetOptions,
} from './date-time-format-constants';

interface AdvancedFormatDialogProps {
  open: boolean;
  onClose: () => void;
  fieldName: string;
  fieldLabel: string;
  existingConfig: Record<string, FieldFormatConfig>;
  onSave: (config: Record<string, FieldFormatConfig>) => void;
  sampleValue?: string;
}

interface FormatStep {
  id: string;
  filterType: string;
  filterParams: Record<string, string>;
  formatType: string;
  formatParams: Record<string, string>;
}

// ── Filter Condition Groups ──

interface FilterOption {
  api_name: string;
  display_label: string;
  description: string;
  icon: typeof Type;
  needsParam: boolean;
}

const FILTER_GROUPS: { label: string; icon: typeof Type; items: FilterOption[] }[] = [
  {
    label: 'Existence',
    icon: Search,
    items: [
      { api_name: 'exists', display_label: 'Not Empty', description: 'Value is present and not empty', icon: Search, needsParam: false },
      { api_name: 'not_exists', display_label: 'Empty', description: 'Value is missing or empty', icon: Search, needsParam: false },
    ],
  },
  {
    label: 'Text',
    icon: Type,
    items: [
      { api_name: 'equals', display_label: 'Equals', description: 'Exact text match', icon: Type, needsParam: true },
      { api_name: 'not_equals', display_label: 'Not Equals', description: 'Does not match exactly', icon: Type, needsParam: true },
      { api_name: 'contains', display_label: 'Contains', description: 'Text includes substring', icon: Type, needsParam: true },
      { api_name: 'not_contains', display_label: 'Not Contains', description: 'Text does not include substring', icon: Type, needsParam: true },
      { api_name: 'starts_with', display_label: 'Starts With', description: 'Text begins with prefix', icon: Type, needsParam: true },
      { api_name: 'not_starts_with', display_label: 'Not Starts With', description: 'Text does not begin with prefix', icon: Type, needsParam: true },
      { api_name: 'ends_with', display_label: 'Ends With', description: 'Text ends with suffix', icon: Type, needsParam: true },
      { api_name: 'not_ends_with', display_label: 'Not Ends With', description: 'Text does not end with suffix', icon: Type, needsParam: true },
      { api_name: 'any_of', display_label: 'Value Is One Of', description: 'Matches any in comma-separated list', icon: Type, needsParam: true },
    ],
  },
  {
    label: 'Number',
    icon: Hash,
    items: [
      { api_name: 'greater_than', display_label: 'Greater Than', description: 'Numeric value is above threshold', icon: Hash, needsParam: true },
      { api_name: 'less_than', display_label: 'Less Than', description: 'Numeric value is below threshold', icon: Hash, needsParam: true },
    ],
  },
  {
    label: 'Boolean',
    icon: ToggleLeft,
    items: [
      { api_name: 'is_true', display_label: 'Is True', description: 'Boolean value is true', icon: ToggleLeft, needsParam: false },
      { api_name: 'is_false', display_label: 'Is False', description: 'Boolean value is false', icon: ToggleLeft, needsParam: false },
    ],
  },
];

// ── Format Transformation Groups ──

interface FormatOption {
  api_name: string;
  display_label: string;
  description: string;
  icon: typeof Type;
  params: { key: string; placeholder: string }[];
}

const FORMAT_GROUPS: { label: string; icon: typeof Type; items: FormatOption[] }[] = [
  {
    label: 'Text Transform',
    icon: Type,
    items: [
      { api_name: 'capitalize', display_label: 'Capitalize', description: 'Capitalize first letter', icon: Type, params: [] },
      { api_name: 'lowercase', display_label: 'Lowercase', description: 'Convert to lowercase', icon: Type, params: [] },
      { api_name: 'titlecase', display_label: 'Title Case', description: 'Capitalize each word', icon: Type, params: [] },
      { api_name: 'trim', display_label: 'Trim', description: 'Remove leading/trailing spaces', icon: Type, params: [] },
      { api_name: 'trim_all', display_label: 'Trim All Whitespace', description: 'Remove all extra whitespace', icon: Type, params: [] },
      { api_name: 'replace', display_label: 'Replace Text', description: 'Find and replace text', icon: Replace, params: [{ key: 'param1', placeholder: 'Find text...' }, { key: 'param2', placeholder: 'Replace with...' }] },
      { api_name: 'replace_value', display_label: 'Change Value', description: 'Replace with a new value', icon: Replace, params: [{ key: 'param1', placeholder: 'New value...' }] },
      { api_name: 'prefix', display_label: 'Add Prefix', description: 'Add text before value', icon: Type, params: [{ key: 'param1', placeholder: 'Prefix text...' }] },
      { api_name: 'postfix', display_label: 'Add Postfix', description: 'Add text after value', icon: Type, params: [{ key: 'param1', placeholder: 'Postfix text...' }] },
      { api_name: 'default', display_label: 'Default Value', description: 'Fallback when empty', icon: Type, params: [{ key: 'param1', placeholder: 'Default value...' }] },
    ],
  },
  {
    label: 'Extract',
    icon: Scissors,
    items: [
      { api_name: 'extract_email', display_label: 'Extract Email', description: 'Pull email address from text', icon: AtSign, params: [] },
      { api_name: 'extract_phone_number', display_label: 'Extract Phone', description: 'Pull phone number from text', icon: Phone, params: [] },
      { api_name: 'extract_url', display_label: 'Extract URL', description: 'Pull URL from text', icon: Link2, params: [] },
      { api_name: 'extract_number', display_label: 'Extract Number', description: 'Pull numeric value from text', icon: Hash, params: [] },
    ],
  },
  {
    label: 'Split & Slice',
    icon: Scissors,
    items: [
      { api_name: 'split', display_label: 'Split', description: 'Split text by delimiter', icon: Scissors, params: [{ key: 'param1', placeholder: 'Delimiter...' }, { key: 'param2', placeholder: 'Index (0-based)...' }] },
      { api_name: 'slice', display_label: 'Slice', description: 'Extract substring by position', icon: Scissors, params: [{ key: 'param1', placeholder: 'Start index...' }, { key: 'param2', placeholder: 'End index...' }] },
      { api_name: 'substr', display_label: 'Substring', description: 'Extract part of text', icon: Scissors, params: [{ key: 'param1', placeholder: 'Start index...' }, { key: 'param2', placeholder: 'Length...' }] },
    ],
  },
  {
    label: 'Number & Math',
    icon: Hash,
    items: [
      { api_name: 'parse_int', display_label: 'Parse Number', description: 'Convert text to number', icon: Hash, params: [] },
      { api_name: 'parse_text', display_label: 'Parse Text', description: 'Convert to text string', icon: Type, params: [] },
      { api_name: 'length', display_label: 'Length', description: 'Get text length', icon: Hash, params: [] },
      { api_name: 'word_count', display_label: 'Word Count', description: 'Count words in text', icon: Hash, params: [] },
      { api_name: 'math', display_label: 'Math Operation', description: 'Arithmetic on numeric value', icon: Hash, params: [{ key: 'param1', placeholder: 'Operator (+,-,*,/)...' }, { key: 'param2', placeholder: 'Value...' }] },
    ],
  },
  {
    label: 'Encoding',
    icon: Link2,
    items: [
      { api_name: 'url_encode', display_label: 'URL Encode', description: 'Encode for URL usage', icon: Link2, params: [] },
      { api_name: 'url_decode', display_label: 'URL Decode', description: 'Decode URL-encoded text', icon: Link2, params: [] },
    ],
  },
  {
    label: 'Date & Time',
    icon: Calendar,
    items: [
      { api_name: 'dateFormats', display_label: 'Date Format', description: 'Convert date format with timezone', icon: Calendar, params: [] },
      { api_name: 'timeFormats', display_label: 'Time Format', description: 'Convert time format with timezone', icon: Clock, params: [] },
      { api_name: 'dateTimeFormats', display_label: 'Date Time Format', description: 'Convert date & time format with timezone', icon: Calendar, params: [] },
    ],
  },
];

// Flat lookup maps
const ALL_FILTERS = FILTER_GROUPS.flatMap((g) => g.items);
const ALL_FORMATS = FORMAT_GROUPS.flatMap((g) => g.items);

// ── Grouped Select Popover ──

interface GroupedSelectProps<T extends { api_name: string; display_label: string; description: string; icon: typeof Type }> {
  groups: { label: string; icon: typeof Type; items: T[] }[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
}

function GroupedSelect<T extends { api_name: string; display_label: string; description: string; icon: typeof Type }>({
  groups,
  value,
  onChange,
  placeholder,
}: GroupedSelectProps<T>) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const allItems = groups.flatMap((g) => g.items);
  const current = allItems.find((item) => item.api_name === value);

  // Update dropdown position when opening
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 240; // max-h-[240px]
    const placeAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
    setDropdownPos({
      top: placeAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: rect.width,
    });
  }, [open]);

  // Close on outside click — check both trigger and portal dropdown
  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        triggerRef.current?.contains(target) ||
        dropdownRef.current?.contains(target)
      ) {
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  // Close when the dialog scrolls (so the dropdown doesn't float detached)
  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const scrollParent = triggerRef.current.closest('.overflow-y-auto');
    if (!scrollParent) return;
    const handleScroll = () => setOpen(false);
    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollParent.removeEventListener('scroll', handleScroll);
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-left text-[11px] transition-colors hover:bg-muted/50 focus:outline-none focus:ring-1 focus:ring-primary"
      >
        {current ? (
          <>
            <current.icon className="h-3 w-3 shrink-0 text-muted-foreground" />
            <span className="min-w-0 flex-1 truncate font-medium text-foreground">{current.display_label}</span>
          </>
        ) : (
          <span className="text-muted-foreground">{placeholder}</span>
        )}
        <ChevronDown className={`h-3 w-3 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed max-h-[240px] overflow-y-auto rounded-lg border border-border bg-background shadow-lg animate-fade-in"
            style={{
              top: dropdownPos.top,
              left: dropdownPos.left,
              width: dropdownPos.width,
              zIndex: 9999,
            }}
          >
            {groups.map((group) => (
              <div key={group.label}>
                <div className="sticky top-0 z-10 bg-muted/80 px-2.5 py-1 backdrop-blur-sm">
                  <div className="flex items-center gap-1">
                    <group.icon className="h-2.5 w-2.5 text-muted-foreground" />
                    <span className="text-[8px] font-bold uppercase tracking-wider text-muted-foreground">
                      {group.label}
                    </span>
                  </div>
                </div>
                {group.items.map((item) => {
                  const isSelected = item.api_name === value;
                  return (
                    <button
                      key={item.api_name}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        onChange(item.api_name);
                        setOpen(false);
                      }}
                      className={`flex w-full items-start gap-1.5 px-2.5 py-1.5 text-left transition-colors ${
                        isSelected ? 'bg-primary/8 text-foreground' : 'hover:bg-muted/50'
                      }`}
                    >
                      <item.icon className={`mt-px h-3 w-3 shrink-0 ${isSelected ? 'text-primary' : 'text-muted-foreground/50'}`} />
                      <div className="min-w-0 flex-1">
                        <p className={`text-[10px] font-medium ${isSelected ? 'text-primary' : 'text-foreground'}`}>
                          {item.display_label}
                        </p>
                        <p className="text-[9px] text-muted-foreground/60">
                          {item.description}
                        </p>
                      </div>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>,
          document.body
        )}
    </div>
  );
}

// ── Searchable Picklist (for timezone & format presets) ──

function SearchablePicklist({
  options,
  value,
  onChange,
  placeholder,
  icon: Icon,
}: {
  options: { display_label: string; api_name: string }[];
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  icon?: typeof Type;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const triggerRef = useRef<HTMLButtonElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0 });

  const current = options.find((o) => o.api_name === value);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.display_label.toLowerCase().includes(q) || o.api_name.toLowerCase().includes(q));
  }, [options, search]);

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const dropdownHeight = 220;
    const placeAbove = spaceBelow < dropdownHeight && rect.top > dropdownHeight;
    setDropdownPos({
      top: placeAbove ? rect.top - dropdownHeight - 4 : rect.bottom + 4,
      left: rect.left,
      width: Math.max(rect.width, 280),
    });
  }, [open]);

  useEffect(() => {
    if (open) {
      setSearch('');
      setTimeout(() => searchInputRef.current?.focus(), 50);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (triggerRef.current?.contains(target) || dropdownRef.current?.contains(target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  useEffect(() => {
    if (!open || !triggerRef.current) return;
    const scrollParent = triggerRef.current.closest('.overflow-y-auto');
    if (!scrollParent) return;
    const handleScroll = () => setOpen(false);
    scrollParent.addEventListener('scroll', handleScroll, { passive: true });
    return () => scrollParent.removeEventListener('scroll', handleScroll);
  }, [open]);

  return (
    <div className="relative">
      <button
        ref={triggerRef}
        onClick={() => setOpen(!open)}
        className="flex w-full items-center gap-1.5 rounded-md border border-border bg-background px-2 py-1.5 text-left text-[11px] transition-colors hover:bg-muted/50 focus:outline-none focus:ring-1 focus:ring-primary"
      >
        {Icon && <Icon className="h-3 w-3 shrink-0 text-muted-foreground" />}
        {current ? (
          <span className="min-w-0 flex-1 truncate font-medium text-foreground font-mono text-[10px]">{current.display_label}</span>
        ) : (
          <span className="min-w-0 flex-1 truncate text-muted-foreground">{placeholder}</span>
        )}
        <ChevronDown className={`h-3 w-3 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open &&
        createPortal(
          <div
            ref={dropdownRef}
            className="fixed flex flex-col max-h-[220px] rounded-lg border border-border bg-background shadow-lg animate-fade-in"
            style={{ top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999 }}
          >
            <div className="flex items-center gap-1.5 border-b border-border px-2 py-1.5">
              <Search className="h-3 w-3 shrink-0 text-muted-foreground" />
              <input
                ref={searchInputRef}
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search..."
                className="flex-1 bg-transparent text-[11px] text-foreground placeholder:text-muted-foreground/50 focus:outline-none"
              />
            </div>
            <div className="flex-1 overflow-y-auto">
              {filtered.length === 0 && (
                <div className="px-2 py-3 text-center text-[10px] text-muted-foreground">No results found</div>
              )}
              {filtered.map((option) => {
                const isSelected = option.api_name === value;
                return (
                  <button
                    key={option.api_name}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      onChange(option.api_name);
                      setOpen(false);
                    }}
                    className={`flex w-full items-center gap-1.5 px-2.5 py-1.5 text-left text-[10px] font-mono transition-colors ${
                      isSelected ? 'bg-primary/8 text-primary font-medium' : 'hover:bg-muted/50 text-foreground'
                    }`}
                  >
                    <span className="min-w-0 flex-1 truncate">{option.display_label}</span>
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}

// ── Main Dialog ──

export function AdvancedFormatDialog({
  open,
  onClose,
  fieldName,
  fieldLabel,
  existingConfig,
  onSave,
  sampleValue,
}: AdvancedFormatDialogProps) {
  const [steps, setSteps] = useState<FormatStep[]>([]);
  const [testInput, setTestInput] = useState('');

  // Drag-and-drop reorder state
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dropIdx, setDropIdx] = useState<number | null>(null);

  // Initialize steps + test input from existing config
  useEffect(() => {
    if (!open) return;

    setTestInput(sampleValue || '');

    const existingSteps: FormatStep[] = [];
    Object.entries(existingConfig).forEach(([stepKey, config]) => {
      existingSteps.push({
        id: stepKey,
        filterType: config.filter?.[0]?.type || 'exists',
        filterParams: (config.filter?.[0]?.params as Record<string, string>) || {},
        formatType: config.format?.type || 'parse_text',
        formatParams: (config.format?.params as Record<string, string>) || {},
      });
    });

    if (existingSteps.length === 0) {
      existingSteps.push({
        id: '1',
        filterType: 'exists',
        filterParams: {},
        formatType: 'parse_text',
        formatParams: {},
      });
    }

    setSteps(existingSteps);
  }, [open, existingConfig]);

  const addStep = () => {
    const newId = String(Date.now());
    setSteps([
      ...steps,
      {
        id: newId,
        filterType: 'exists',
        filterParams: {},
        formatType: 'parse_text',
        formatParams: {},
      },
    ]);
  };

  const removeStep = (id: string) => {
    setSteps(steps.filter((s) => s.id !== id));
  };

  const updateStep = (id: string, updates: Partial<FormatStep>) => {
    setSteps(steps.map((s) => (s.id === id ? { ...s, ...updates } : s)));
  };

  // ── Drag-and-drop reorder handlers ──
  const handleStepDragStart = (e: React.DragEvent, index: number) => {
    setDragIdx(index);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(index));
    // Make the drag image slightly transparent
    const target = e.currentTarget as HTMLElement;
    requestAnimationFrame(() => {
      target.style.opacity = '0.4';
    });
  };

  const handleStepDragEnd = (e: React.DragEvent) => {
    (e.currentTarget as HTMLElement).style.opacity = '1';
    setDragIdx(null);
    setDropIdx(null);
  };

  const handleStepDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragIdx !== null && index !== dragIdx) {
      setDropIdx(index);
    }
  };

  const handleStepDrop = (e: React.DragEvent, toIndex: number) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === toIndex) {
      setDragIdx(null);
      setDropIdx(null);
      return;
    }

    const reordered = [...steps];
    const [moved] = reordered.splice(dragIdx, 1);
    reordered.splice(toIndex, 0, moved);
    setSteps(reordered);
    setDragIdx(null);
    setDropIdx(null);
  };

  const handleSave = () => {
    const config: Record<string, FieldFormatConfig> = {};
    steps.forEach((step, idx) => {
      config[String(idx + 1)] = {
        quickFormat: step.formatType,
        filter: [{ type: step.filterType, params: step.filterParams }],
        format: { type: step.formatType, params: step.formatParams },
      };
    });
    onSave(config);
    onClose();
  };

  // Look up current filter/format option for descriptions
  const getFilterOption = (apiName: string) => ALL_FILTERS.find((f) => f.api_name === apiName);
  const getFormatOption = (apiName: string) => ALL_FORMATS.find((f) => f.api_name === apiName);

  // ── Simulate pipeline on test input ──
  const stepPreviews = simulatePipeline(testInput, steps);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 animate-fade-in">
      <div className="mx-4 flex max-h-[80vh] w-full max-w-md flex-col rounded-xl border border-border bg-background shadow-2xl">
        {/* ─── Header ─── */}
        <div className="flex items-center justify-between border-b border-border px-4 py-2.5">
          <div className="flex items-center gap-2">
            <div className="flex h-6 w-6 items-center justify-center rounded-md bg-primary/10">
              <Wand2 className="h-3 w-3 text-primary" />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-foreground">
                Advanced Format Pipeline
              </h3>
              <p className="text-[10px] text-muted-foreground">
                {fieldLabel}
                <span className="ml-1 font-mono text-muted-foreground/50">({fieldName})</span>
              </p>
            </div>
          </div>
          <Button variant="ghost" size="icon-sm" onClick={onClose}>
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>

        {/* ─── Pipeline description + test input ─── */}
        <div className="border-b border-border/50 bg-muted/30 px-4 py-1.5 space-y-1.5">
          <p className="text-[10px] text-muted-foreground">
            Each step first <span className="font-medium text-foreground">filters</span> the value, then <span className="font-medium text-foreground">transforms</span> it. First matching filter applies.
          </p>
          <div className="flex items-center gap-1.5">
            <Eye className="h-3 w-3 shrink-0 text-muted-foreground/60" />
            <input
              type="text"
              value={testInput}
              onChange={(e) => setTestInput(e.target.value)}
              placeholder="Enter a test value to preview pipeline..."
              className="flex-1 rounded border border-border bg-background px-2 py-1 text-[10px] font-mono text-foreground placeholder:text-muted-foreground/50 placeholder:font-sans focus:outline-none focus:ring-1 focus:ring-primary"
            />
          </div>
        </div>

        {/* ─── Steps Pipeline ─── */}
        <div className="flex-1 overflow-y-auto px-4 py-3 scrollbar-thin">
          {steps.map((step, index) => {
            const filterOption = getFilterOption(step.filterType);
            const formatOption = getFormatOption(step.formatType);

            return (
              <div
                key={step.id}
                draggable={steps.length > 1}
                onDragStart={(e) => handleStepDragStart(e, index)}
                onDragEnd={handleStepDragEnd}
                onDragOver={(e) => handleStepDragOver(e, index)}
                onDrop={(e) => handleStepDrop(e, index)}
              >
                {/* Pipeline connector */}
                {index > 0 && (
                  <div className="flex flex-col items-center py-0.5">
                    <div className="h-2.5 w-px bg-border" />
                    <ArrowDown className="h-3 w-3 text-muted-foreground/40" />
                    <div className="h-1.5 w-px bg-border" />
                  </div>
                )}

                {/* Step Card */}
                <div className={`relative rounded-lg border bg-background shadow-sm transition-all duration-150 ${
                  dropIdx === index && dragIdx !== null && dragIdx !== index
                    ? 'border-primary ring-1 ring-primary/30'
                    : 'border-border'
                }`}>
                  {/* Step header */}
                  <div className="flex items-center gap-1.5 border-b border-border/50 px-2.5 py-1.5">
                    <GripVertical className={`h-3 w-3 shrink-0 ${steps.length > 1 ? 'cursor-grab active:cursor-grabbing text-muted-foreground/50 hover:text-muted-foreground' : 'text-muted-foreground/20'}`} />
                    <div className="flex h-4 w-4 items-center justify-center rounded-full bg-primary/10 text-[9px] font-bold text-primary">
                      {index + 1}
                    </div>
                    <span className="flex-1 text-[10px] font-semibold text-foreground">
                      Step {index + 1}
                    </span>

                    {steps.length > 1 && (
                      <button
                        onClick={() => removeStep(step.id)}
                        className="rounded p-0.5 text-muted-foreground/30 transition-colors hover:bg-destructive/10 hover:text-destructive"
                        title="Remove step"
                      >
                        <Trash2 className="h-3 w-3" />
                      </button>
                    )}
                  </div>

                  <div className="p-2.5 space-y-2.5">
                    {/* ── Filter Condition ── */}
                    <div>
                      <div className="mb-1.5 flex items-center gap-1">
                        <Filter className="h-2.5 w-2.5 text-amber-500" />
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-amber-600 dark:text-amber-400">
                          If value...
                        </span>
                      </div>

                      <GroupedSelect
                        groups={FILTER_GROUPS}
                        value={step.filterType}
                        onChange={(val) => updateStep(step.id, { filterType: val, filterParams: {} })}
                        placeholder="Select filter condition..."
                      />

                      {/* Filter parameter input */}
                      {filterOption?.needsParam && (
                        <input
                          type="text"
                          value={step.filterParams.param1 || ''}
                          onChange={(e) =>
                            updateStep(step.id, {
                              filterParams: { ...step.filterParams, param1: e.target.value },
                            })
                          }
                          placeholder={
                            step.filterType === 'any_of'
                              ? 'Comma-separated values...'
                              : 'Comparison value...'
                          }
                          className="mt-1.5 w-full rounded-md border border-border bg-muted/30 px-2 py-1.5 text-[11px] text-foreground font-mono placeholder:text-muted-foreground/50 placeholder:font-sans focus:outline-none focus:ring-1 focus:ring-primary"
                        />
                      )}
                    </div>

                    {/* ── Visual Connector ── */}
                    <div className="flex items-center gap-1.5 px-1">
                      <div className="flex-1 border-t border-dashed border-border/60" />
                      <span className="text-[8px] font-bold uppercase tracking-wider text-muted-foreground/40">then</span>
                      <div className="flex-1 border-t border-dashed border-border/60" />
                    </div>

                    {/* ── Format Transformation ── */}
                    <div>
                      <div className="mb-1.5 flex items-center gap-1">
                        <Wand2 className="h-2.5 w-2.5 text-primary" />
                        <span className="text-[9px] font-semibold uppercase tracking-wider text-primary">
                          Transform...
                        </span>
                      </div>

                      <GroupedSelect
                        groups={FORMAT_GROUPS}
                        value={step.formatType}
                        onChange={(val) => updateStep(step.id, { formatType: val, formatParams: {} })}
                        placeholder="Select transformation..."
                      />

                      {/* Format parameter inputs — date/time types get timezone + preset dropdowns */}
                      {isDateTimeFormatType(step.formatType) ? (
                        <div className="mt-1.5 space-y-1.5">
                          <SearchablePicklist
                            options={TIMEZONE_OPTIONS}
                            value={step.formatParams.param1 || ''}
                            onChange={(val) =>
                              updateStep(step.id, {
                                formatParams: { ...step.formatParams, param1: val },
                              })
                            }
                            placeholder="Select timezone..."
                            icon={Globe}
                          />
                          <SearchablePicklist
                            options={getFormatPresetOptions(step.formatType)}
                            value={step.formatParams.param2 || ''}
                            onChange={(val) =>
                              updateStep(step.id, {
                                formatParams: { ...step.formatParams, param2: val },
                              })
                            }
                            placeholder="Select format..."
                            icon={Calendar}
                          />
                        </div>
                      ) : formatOption && formatOption.params.length > 0 ? (
                        <div className="mt-1.5 space-y-1.5">
                          {formatOption.params.map((param) => (
                            <input
                              key={param.key}
                              type="text"
                              value={step.formatParams[param.key] || ''}
                              onChange={(e) =>
                                updateStep(step.id, {
                                  formatParams: { ...step.formatParams, [param.key]: e.target.value },
                                })
                              }
                              placeholder={param.placeholder}
                              className="w-full rounded-md border border-border bg-muted/30 px-2 py-1.5 text-[11px] text-foreground font-mono placeholder:text-muted-foreground/50 placeholder:font-sans focus:outline-none focus:ring-1 focus:ring-primary"
                            />
                          ))}
                        </div>
                      ) : null}
                    </div>

                    {/* ── Sample Preview ── */}
                    {testInput !== '' && stepPreviews[index] && (
                      <div className="rounded-md border border-border/50 bg-muted/20 px-2 py-1.5">
                        <div className="flex items-center gap-1 mb-1">
                          <Eye className="h-2.5 w-2.5 text-muted-foreground/60" />
                          <span className="text-[8px] font-semibold uppercase tracking-wider text-muted-foreground/60">
                            Preview
                          </span>
                          {!stepPreviews[index].filterMatch && (
                            <span className="ml-auto text-[8px] text-amber-500 font-medium">filter skipped</span>
                          )}
                        </div>
                        <p className={`text-[10px] font-mono break-all leading-relaxed ${
                          stepPreviews[index].filterMatch ? 'text-foreground' : 'text-muted-foreground/50 line-through'
                        }`}>
                          {stepPreviews[index].output || '(empty)'}
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          {/* Add Step connector + button */}
          <div className="mt-1 flex flex-col items-center">
            <div className="h-3 w-px bg-border" />
            <button
              onClick={addStep}
              className="flex items-center gap-1 rounded-full border border-dashed border-border bg-background px-3 py-1.5 text-[10px] font-medium text-muted-foreground transition-all hover:border-primary hover:bg-primary/5 hover:text-primary"
            >
              <Plus className="h-3 w-3" />
              Add Step
            </button>
          </div>
        </div>

        {/* ─── Footer ─── */}
        <div className="flex items-center justify-between border-t border-border px-4 py-2.5">
          <button
            onClick={() => { onSave({}); onClose(); }}
            className="flex items-center gap-1 text-[9px] text-destructive/70 hover:text-destructive transition-colors"
          >
            <Trash2 className="h-3 w-3" />
            Clear All
          </button>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" onClick={onClose}>
              Cancel
            </Button>
            <Button size="sm" onClick={handleSave}>
              Save Pipeline
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Pipeline simulation for live preview ──

interface StepPreview {
  input: string;
  output: string;
  filterMatch: boolean;
}

function simulatePipeline(input: string, steps: FormatStep[]): StepPreview[] {
  const previews: StepPreview[] = [];
  let currentValue = input;

  for (const step of steps) {
    const filterMatch = evaluateFilter(currentValue, step.filterType, step.filterParams);
    let output = currentValue;

    if (filterMatch) {
      output = applyFormat(currentValue, step.formatType, step.formatParams);
    }

    previews.push({ input: currentValue, output, filterMatch });
    currentValue = output;
  }

  return previews;
}

function evaluateFilter(value: string, filterType: string, params: Record<string, string>): boolean {
  const p1 = params.param1 || '';

  switch (filterType) {
    case 'exists':
      return value !== '' && value !== null && value !== undefined;
    case 'not_exists':
      return value === '' || value === null || value === undefined;
    case 'equals':
      return value === p1;
    case 'not_equals':
      return value !== p1;
    case 'contains':
      return value.includes(p1);
    case 'not_contains':
      return !value.includes(p1);
    case 'starts_with':
      return value.startsWith(p1);
    case 'not_starts_with':
      return !value.startsWith(p1);
    case 'ends_with':
      return value.endsWith(p1);
    case 'not_ends_with':
      return !value.endsWith(p1);
    case 'any_of':
      return p1.split(',').map((s) => s.trim()).includes(value);
    case 'greater_than':
      return Number(value) > Number(p1);
    case 'less_than':
      return Number(value) < Number(p1);
    case 'is_true':
      return value === 'true' || value === '1';
    case 'is_false':
      return value === 'false' || value === '0';
    default:
      return true;
  }
}

function applyFormat(value: string, formatType: string, params: Record<string, string>): string {
  const p1 = params.param1 || '';
  const p2 = params.param2 || '';

  try {
    switch (formatType) {
      case 'parse_text':
        return value;
      case 'parse_int':
        return String(parseInt(value, 10) || 0);
      case 'parse_json':
        return JSON.stringify(JSON.parse(value));
      case 'parse_array':
        return JSON.stringify(value.split(',').map((s) => s.trim()));
      case 'capitalize':
        return value.charAt(0).toUpperCase() + value.slice(1);
      case 'lowercase':
        return value.toLowerCase();
      case 'titlecase':
        return value.replace(/\b\w/g, (c) => c.toUpperCase());
      case 'trim':
        return value.trim();
      case 'trim_all':
        return value.replace(/\s+/g, ' ').trim();
      case 'replace':
        return p1 ? value.split(p1).join(p2) : value;
      case 'replace_value':
        return p1;
      case 'prefix':
        return p1 + value;
      case 'postfix':
        return value + p1;
      case 'default':
        return value || p1;
      case 'extract_email': {
        const emailMatch = value.match(/[\w.+-]+@[\w-]+\.[\w.]+/);
        return emailMatch ? emailMatch[0] : '';
      }
      case 'extract_phone_number': {
        const phoneMatch = value.match(/[+]?[\d\s()-]{7,}/);
        return phoneMatch ? phoneMatch[0].trim() : '';
      }
      case 'extract_url': {
        const urlMatch = value.match(/https?:\/\/[^\s]+/);
        return urlMatch ? urlMatch[0] : '';
      }
      case 'extract_number': {
        const numMatch = value.match(/-?\d+(\.\d+)?/);
        return numMatch ? numMatch[0] : '';
      }
      case 'split': {
        const parts = value.split(p1 || ',');
        const idx = parseInt(p2, 10) || 0;
        return parts[idx] ?? '';
      }
      case 'slice': {
        const start = parseInt(p1, 10) || 0;
        const end = p2 ? parseInt(p2, 10) : undefined;
        return value.slice(start, end);
      }
      case 'substr': {
        const sStart = parseInt(p1, 10) || 0;
        const sLen = p2 ? parseInt(p2, 10) : undefined;
        return sLen !== undefined ? value.substring(sStart, sStart + sLen) : value.substring(sStart);
      }
      case 'length':
        return String(value.length);
      case 'word_count':
        return String(value.trim() ? value.trim().split(/\s+/).length : 0);
      case 'math': {
        const num = parseFloat(value) || 0;
        const operand = parseFloat(p2) || 0;
        switch (p1) {
          case '+': return String(num + operand);
          case '-': return String(num - operand);
          case '*': return String(num * operand);
          case '/': return operand !== 0 ? String(num / operand) : 'Infinity';
          default: return String(num);
        }
      }
      case 'url_encode':
        return encodeURIComponent(value);
      case 'url_decode':
        return decodeURIComponent(value);
      case 'dateFormats':
      case 'timeFormats':
      case 'dateTimeFormats': {
        // Preview date/time formatting using browser Intl API
        const tz = p1 || undefined;
        const fmt = p2 || '';
        if (fmt === 'timestamp') {
          try {
            const d = new Date(value);
            return isNaN(d.getTime()) ? value : String(d.getTime());
          } catch { return value; }
        }
        if (!fmt || !tz) return value;
        try {
          const d = new Date(value);
          if (isNaN(d.getTime())) return value;
          // Use Intl to show a timezone-aware preview
          const options: Intl.DateTimeFormatOptions = { timeZone: tz };
          if (formatType === 'timeFormats') {
            options.hour = '2-digit'; options.minute = '2-digit'; options.second = '2-digit';
            options.hour12 = fmt.includes('A') || fmt.includes('a');
            options.timeZoneName = fmt.includes('Z') ? 'short' : undefined;
          } else if (formatType === 'dateFormats') {
            options.year = 'numeric'; options.month = '2-digit'; options.day = '2-digit';
          } else {
            options.year = 'numeric'; options.month = '2-digit'; options.day = '2-digit';
            options.hour = '2-digit'; options.minute = '2-digit';
            options.hour12 = fmt.includes('A') || fmt.includes('a');
            options.timeZoneName = fmt.includes('Z') ? 'short' : undefined;
          }
          return new Intl.DateTimeFormat('en-US', options).format(d) + ` (${fmt})`;
        } catch { return value; }
      }
      default:
        return value;
    }
  } catch {
    return value;
  }
}
